import { ArrowLeftIcon, CheckIcon } from 'lucide-react';
import Link from 'next/link';
import React from 'react';

import { Skeleton, Text } from '@/design-system/components/ui';

const BOTTOM_BAR_HEIGHT = 52;
const TOP_BAR_HEIGHT = 64;

export interface StepConfig {
  label: string;
}

interface Props {
  children: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  backButton?: { text: string; href: string; onClick?: (e: React.MouseEvent) => void };
  isLoading?: boolean;
  actions?: React.ReactNode;
  steps: StepConfig[];
  activeStep: number;
}

interface StepIndicatorProps {
  active: boolean;
  completed: boolean;
  stepNumber: number;
}

const StepIndicator = ({ active, completed, stepNumber }: StepIndicatorProps) => (
  <div
    className={`tw:flex tw:items-center tw:justify-center tw:w-8 tw:h-8 tw:rounded-full tw:shrink-0 tw:text-sm tw:font-semibold ${
      active || completed
        ? 'tw:bg-fuul-blue tw:text-white'
        : 'tw:bg-neutral-200 dark:tw:bg-neutral-700 tw:text-neutral-500 dark:tw:text-neutral-400'
    }`}
  >
    {completed ? <CheckIcon size={16} /> : stepNumber}
  </div>
);

const StepConnector = ({ active }: { active: boolean }) => (
  <div className={`tw:h-0.5 tw:w-full tw:rounded ${active ? 'tw:bg-fuul-blue' : 'tw:bg-border'}`} />
);

export const StepperFormPageTemplate = ({
  children,
  title,
  subtitle,
  backButton,
  isLoading,
  actions,
  steps,
  activeStep,
}: Props) => {
  return (
    <>
      <header className="tw:sticky tw:z-[1099] tw:bg-background tw:pb-4" style={{ top: TOP_BAR_HEIGHT }}>
        <div className="tw:max-w-[1440px] tw:mx-auto tw:px-6">
          <div className="tw:flex tw:flex-col tw:gap-4 tw:items-start">
            <div className="tw:flex tw:flex-col tw:gap-1 tw:items-start">
              {backButton && (
                <Link
                  href={backButton.href}
                  onClick={backButton.onClick}
                  className="tw:inline-flex tw:items-center tw:no-underline tw:text-inherit tw:hover:underline"
                >
                  <ArrowLeftIcon className="tw:mr-2 tw:w-6 tw:h-6" />
                  <Text variant="body-md" weight="semibold">
                    {backButton.text}
                  </Text>
                </Link>
              )}

              {isLoading ? (
                <div className="tw:flex tw:flex-col tw:gap-1">
                  <Skeleton variant="text" width={180} height={36} />
                  <Skeleton variant="text" width={240} height={20} />
                </div>
              ) : (
                <>
                  {title && (
                    <Text variant="display-sm" as="h1" className="tw:m-0">
                      {title}
                    </Text>
                  )}
                  {subtitle && (
                    <Text variant="body-md" color="muted">
                      {subtitle}
                    </Text>
                  )}
                </>
              )}
            </div>

            {/* Stepper */}
            <div className="tw:flex tw:flex-row tw:items-center tw:w-full tw:max-w-[840px] tw:self-center">
              {steps.map((step, index) => {
                const isActive = index === activeStep;
                const isCompleted = index < activeStep;
                return (
                  <React.Fragment key={step.label}>
                    <div className="tw:flex tw:flex-row tw:items-center tw:shrink-0">
                      <StepIndicator active={isActive} completed={isCompleted} stepNumber={index + 1} />
                      {isActive && (
                        <span className="tw:text-xs tw:font-semibold tw:text-fuul-blue tw:pl-2 tw:whitespace-nowrap tw:uppercase">
                          {step.label}
                        </span>
                      )}
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className="tw:flex tw:items-center tw:flex-1 tw:h-8"
                        style={{
                          paddingLeft: isActive ? 16 : 0,
                          paddingRight: index + 1 === activeStep ? 16 : 0,
                        }}
                      >
                        <StepConnector active={isCompleted} />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </header>

      <main
        style={{
          paddingBottom: actions ? `${BOTTOM_BAR_HEIGHT + 16}px` : '2rem',
        }}
      >
        <div className="tw:max-w-[1440px] tw:mx-auto tw:px-6 tw:pt-4">
          <div className="tw:flex tw:flex-col tw:gap-6">{children}</div>
        </div>
      </main>

      {actions && (
        <div className="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:py-2 tw:border-t tw:border-border tw:bg-card tw:z-[1100] tw:lg:pl-[280px]">
          <div className="tw:max-w-[1440px] tw:mx-auto tw:px-6">
            <div className="tw:flex tw:flex-row tw:justify-between tw:items-center">{actions}</div>
          </div>
        </div>
      )}
    </>
  );
};
